import React, { useState, useRef } from 'react';
import './ContactBookDemo.css';

/** Set REACT_APP_WEB3FORMS_ACCESS_KEY in .env.local — see .env.example */
const WEB3FORMS_ACCESS_KEY = process.env.REACT_APP_WEB3FORMS_ACCESS_KEY || '';

const SPACE_TYPES = [
  'Café',
  'Co-working',
  'University',
  'Event',
  'Community',
  'Innovation Hub',
  'Other',
];

const STEPS = [
  {
    title: 'We learn about your space',
    description: 'Tell us how people move through your venue and what engagement looks like today.',
  },
  {
    title: 'We suggest suitable groups and experiences',
    description: 'We map intent-based groups and moments that fit your space.',
  },
  {
    title: 'We help you run a pilot',
    description: 'Launch with light-touch setup — no heavy training or new hardware.',
  },
  {
    title: 'We review what worked',
    description: 'See what resonated and what you would love to double down on.',
  },
];

const IDEAL_FOR = [
  'Café',
  'Co-working',
  'Universities',
  'Events',
  'Communities',
  'Innovation Hubs',
];

const FAQ_ITEMS = [
  {
    question: 'Does my staff need training?',
    answer: 'No. We handle the setup and support.',
  },
  {
    question: 'Do I need additional hardware?',
    answer: 'No. Just simple QR points.',
  },
  {
    question: 'How long does setup take?',
    answer: 'Less than 15 minutes.',
  },
  {
    question: 'Can I test before committing?',
    answer: 'Yes. We encourage it.',
  },
];

const INITIAL_FORM = {
  name: '',
  businessName: '',
  spaceType: '',
  email: '',
  phone: '',
  city: '',
};

const buildMailtoLink = (form) => {
  const subject = encodeURIComponent(`PoppyGrid demo request — ${form.businessName}`);
  const body = encodeURIComponent(
    [
      `Name: ${form.name}`,
      `Business: ${form.businessName}`,
      `Type of space: ${form.spaceType}`,
      `Email: ${form.email}`,
      `Phone: ${form.phone}`,
      `City: ${form.city}`,
    ].join('\n')
  );
  return `mailto:aaron.rebelo@tocoroyo.com?cc=inquire@poppygrid.com&subject=${subject}&body=${body}`;
};

const ContactBookDemo = () => {
  const formRef = useRef(null);
  const [form, setForm] = useState(INITIAL_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [openFaq, setOpenFaq] = useState(null);

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const submitViaMailto = () => {
    window.location.href = buildMailtoLink(form);
    setSubmitStatus('mailto');
    setForm(INITIAL_FORM);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus(null);

    if (!WEB3FORMS_ACCESS_KEY) {
      submitViaMailto();
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = new FormData();
      payload.append('access_key', WEB3FORMS_ACCESS_KEY);
      payload.append('subject', `PoppyGrid demo request — ${form.businessName}`);
      payload.append('from_name', form.name);
      payload.append('email', form.email);
      payload.append('name', form.name);
      payload.append('businessName', form.businessName);
      payload.append('spaceType', form.spaceType);
      payload.append('phone', form.phone);
      payload.append('city', form.city);
      payload.append(
        'message',
        [
          `Name: ${form.name}`,
          `Business: ${form.businessName}`,
          `Type of space: ${form.spaceType}`,
          `Email: ${form.email}`,
          `Phone: ${form.phone}`,
          `City: ${form.city}`,
        ].join('\n')
      );

      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        body: payload,
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Submission failed');
      }

      setSubmitStatus('success');
      setForm(INITIAL_FORM);
    } catch (err) {
      console.error('Demo request error:', err);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleFaq = (index) => {
    setOpenFaq((prev) => (prev === index ? null : index));
  };

  return (
    <div className="contact-page">
      <section className="contact-hero" aria-labelledby="contact-hero-heading">
        <div className="contact-inner contact-hero-inner">
          <p className="contact-eyebrow">Here we turn curiosity into conversation</p>
          <h1 id="contact-hero-heading" className="contact-headline">
            Let's explore your space together
          </h1>
          <p className="contact-subheadline">
            See how PoppyGrid could work in your café, co-working space, community, or event.
          </p>

          <form
            id="demo-form"
            ref={formRef}
            className="contact-form"
            onSubmit={handleSubmit}
          >
            <div className="contact-form-grid">
              <label className="contact-field">
                <span className="contact-label">Name</span>
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  required
                  autoComplete="name"
                  placeholder="Your name"
                />
              </label>
              <label className="contact-field">
                <span className="contact-label">Business name</span>
                <input
                  type="text"
                  name="businessName"
                  value={form.businessName}
                  onChange={handleChange}
                  required
                  placeholder="Your business"
                />
              </label>
              <label className="contact-field">
                <span className="contact-label">Type of space</span>
                <select
                  name="spaceType"
                  value={form.spaceType}
                  onChange={handleChange}
                  required
                >
                  <option value="" disabled>
                    Select a space type
                  </option>
                  {SPACE_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </label>
              <label className="contact-field">
                <span className="contact-label">Email</span>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  required
                  autoComplete="email"
                  placeholder="you@business.com"
                />
              </label>
              <label className="contact-field">
                <span className="contact-label">Phone number</span>
                <input
                  type="tel"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  required
                  autoComplete="tel"
                  placeholder="+91 ..."
                />
              </label>
              <label className="contact-field">
                <span className="contact-label">City</span>
                <input
                  type="text"
                  name="city"
                  value={form.city}
                  onChange={handleChange}
                  required
                  autoComplete="address-level2"
                  placeholder="Your city"
                />
              </label>
            </div>

            {submitStatus === 'success' && (
              <p className="contact-form-message contact-form-message--success" role="status">
                Thanks — we've received your request and will be in touch soon.
              </p>
            )}
            {submitStatus === 'mailto' && (
              <p className="contact-form-message contact-form-message--success" role="status">
                Your email app should open with the details filled in. Hit send there to complete
                your demo request.
              </p>
            )}
            {submitStatus === 'error' && (
              <p className="contact-form-message contact-form-message--error" role="alert">
                Something went wrong. Please try again or email us directly below.
              </p>
            )}

            <button
              type="submit"
              className="contact-btn contact-btn--primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Sending…' : 'Request demo'}
            </button>
          </form>
        </div>
      </section>

      <section className="contact-section" aria-labelledby="contact-steps-heading">
        <div className="contact-inner">
          <h2 id="contact-steps-heading" className="contact-section-title">
            What happens next?
          </h2>
          <ol className="contact-steps">
            {STEPS.map((step, index) => (
              <li key={step.title} className="contact-step">
                <span className="contact-step-num" aria-hidden="true">
                  {index + 1}
                </span>
                <div>
                  <h3 className="contact-step-title">{step.title}</h3>
                  <p className="contact-step-text">{step.description}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="contact-section contact-section--alt" aria-labelledby="contact-ideal-heading">
        <div className="contact-inner">
          <h2 id="contact-ideal-heading" className="contact-section-title contact-section-title--center">
            Ideal for
          </h2>
          <ul className="contact-ideal-grid">
            {IDEAL_FOR.map((label) => (
              <li key={label} className="contact-ideal-pill">
                {label}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="contact-section" aria-labelledby="contact-faq-heading">
        <div className="contact-inner contact-inner--narrow">
          <h2 id="contact-faq-heading" className="contact-section-title contact-section-title--center">
            Frequently asked questions
          </h2>
          <div className="contact-faq">
            {FAQ_ITEMS.map((item, index) => {
              const isOpen = openFaq === index;
              const panelId = `faq-panel-${index}`;
              const buttonId = `faq-button-${index}`;
              return (
                <div key={item.question} className={`contact-faq-item ${isOpen ? 'is-open' : ''}`}>
                  <button
                    type="button"
                    id={buttonId}
                    className="contact-faq-trigger"
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    onClick={() => toggleFaq(index)}
                  >
                    <span>{item.question}</span>
                    <span className="contact-faq-icon" aria-hidden="true" />
                  </button>
                  <div
                    id={panelId}
                    role="region"
                    aria-labelledby={buttonId}
                    className="contact-faq-panel"
                    hidden={!isOpen}
                  >
                    <p>{item.answer}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="contact-section contact-questions" aria-labelledby="contact-questions-heading">
        <div className="contact-inner contact-questions-inner">
          <h2 id="contact-questions-heading" className="contact-section-title contact-section-title--center">
            Questions?
          </h2>
          <div className="contact-channels">
            <div className="contact-channel">
              <span className="contact-channel-label">Email</span>
              <a href="mailto:aaron.rebelo@tocoroyo.com">aaron.rebelo@tocoroyo.com</a>
              <span className="contact-channel-sep">/</span>
              <a href="mailto:inquire@poppygrid.com">inquire@poppygrid.com</a>
            </div>
            <div className="contact-channel">
              <span className="contact-channel-label">WhatsApp</span>
              <a
                href="https://wa.me/917899100223"
                target="_blank"
                rel="noopener noreferrer"
              >
                +91 78991 00223
              </a>
            </div>
          </div>
          <button type="button" className="contact-btn contact-btn--primary" onClick={scrollToForm}>
            Book now
          </button>
        </div>
      </section>
    </div>
  );
};

export default ContactBookDemo;
